import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { ArtistProfile, OrderItem } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import './AdminArtists.css';

interface EditForm {
  displayName: string;
  discountPercent: string;
  artworkUploadLimit: string;
  venmoHandle: string;
}

interface PayoutSelection {
  [orderItemId: number]: boolean;
}

const AdminArtists: React.FC = () => {
  const navigate = useNavigate();
  const [artists, setArtists] = useState<ArtistProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ArtistProfile | null>(null);
  const [payoutSelections, setPayoutSelections] = useState<Record<number, PayoutSelection>>({});
  const [payoutRefs, setPayoutRefs] = useState<Record<number, string>>({});
  const [spotlightArtistId, setSpotlightArtistId] = useState<string>('');
  const [savingProfile, setSavingProfile] = useState<boolean>(false);
  const [savingSpotlight, setSavingSpotlight] = useState<boolean>(false);
  const [markingPaidId, setMarkingPaidId] = useState<number | null>(null);
  const [loadingUnpaidId, setLoadingUnpaidId] = useState<number | null>(null);
  const { success, error: showError, warning } = useToast();
  const [form, setForm] = useState<EditForm>({
    displayName: '',
    discountPercent: '0',
    artworkUploadLimit: '14',
    venmoHandle: ''
  });

  const fetchArtists = async (): Promise<void> => {
    try {
      const response = await api.get<ArtistProfile[]>('/admin/artists');
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtists();
  }, []);

  const openEdit = (artist: ArtistProfile): void => {
    setEditing(artist);
    setForm({
      displayName: artist.displayName || artist.email,
      discountPercent: artist.discountPercent.toString(),
      artworkUploadLimit: artist.artworkUploadLimit.toString(),
      venmoHandle: artist.venmoHandle || ''
    });
  };

  const saveProfile = async (): Promise<void> => {
    if (!editing) return;
    if (!form.displayName.trim()) {
      warning('Display name is required.');
      return;
    }
    const discount = parseFloat(form.discountPercent);
    if (Number.isNaN(discount) || discount < 0 || discount > 100) {
      warning('Discount must be between 0 and 100.');
      return;
    }
    const uploadLimit = parseInt(form.artworkUploadLimit);
    if (Number.isNaN(uploadLimit) || uploadLimit < 1) {
      warning('Upload limit must be at least 1.');
      return;
    }
    setSavingProfile(true);
    try {
      await api.put(`/admin/artists/${editing.userId}`, {
        displayName: form.displayName,
        discountPercent: discount || 0,
        artworkUploadLimit: uploadLimit || 14,
        venmoHandle: form.venmoHandle || null
      });
      setEditing(null);
      fetchArtists();
      success('Artist profile saved.');
    } catch (error) {
      showError('Failed to save artist profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const setArtistOfMonth = async (): Promise<void> => {
    if (!spotlightArtistId) {
      warning('Select an artist first.');
      return;
    }
    setSavingSpotlight(true);
    try {
      await api.post('/art/artist-of-month', { artistId: parseInt(spotlightArtistId) });
      success('Artist of the month updated.');
    } catch (error) {
      showError('Failed to set artist of the month.');
    } finally {
      setSavingSpotlight(false);
    }
  };

  const fetchUnpaidItems = async (artistId: number): Promise<void> => {
    setLoadingUnpaidId(artistId);
    try {
      const response = await api.get<{ orderItems: OrderItem[] }[]>('/orders/admin', {
        params: { artistId, unpaidOnly: true }
      });
      const items = response.data.flatMap((order) => order.orderItems || []);
      const map: PayoutSelection = {};
      items.forEach((item) => { map[item.id] = false; });
      setPayoutSelections((prev) => ({ ...prev, [artistId]: map }));
    } catch (error) {
      console.error('Failed to fetch unpaid items:', error);
      showError('Failed to load unpaid items.');
    } finally {
      setLoadingUnpaidId(null);
    }
  };

  const handlePayoutToggle = (artistId: number, orderItemId: number): void => {
    setPayoutSelections((prev) => ({
      ...prev,
      [artistId]: {
        ...prev[artistId],
        [orderItemId]: !prev[artistId]?.[orderItemId]
      }
    }));
  };

  const handleMarkPaid = async (artistId: number): Promise<void> => {
    const selected = payoutSelections[artistId] || {};
    const orderItemIds = Object.keys(selected)
      .filter((id) => selected[parseInt(id)])
      .map((id) => parseInt(id));
    if (orderItemIds.length === 0) {
      warning('Select at least one item to mark paid.');
      return;
    }
    setMarkingPaidId(artistId);
    try {
      await api.post(`/admin/artists/${artistId}/payout`, {
        orderItemIds,
        venmoReference: payoutRefs[artistId] || null
      });
      fetchArtists();
      fetchUnpaidItems(artistId);
      success('Payout marked as paid.');
    } catch (error) {
      showError('Failed to mark payout.');
    } finally {
      setMarkingPaidId(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading artists...</div>;
  }

  return (
    <div className="admin-artists">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/admin" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Admin Panel
            </Link>
            <h1>Artist Management</h1>
          </div>
        </div>

        <div className="artist-grid">
          {artists.map((artist) => (
            <div key={artist.userId} className="artist-card">
              <h3>{artist.displayName || artist.email}</h3>
              <p><strong>Email:</strong> {artist.email}</p>
              <p><strong>Discount:</strong> {artist.discountPercent}%</p>
              <p><strong>Upload Limit:</strong> {artist.artworkUploadLimit}</p>
              <p><strong>Venmo:</strong> {artist.venmoHandle || 'Not set'}</p>
              <p><strong>Total Earnings:</strong> ${artist.totalEarnings.toFixed(2)}</p>
              <p><strong>Unpaid:</strong> ${artist.unpaidEarnings.toFixed(2)}</p>
              <div className="artist-actions">
                <button className="btn btn-outline" onClick={() => openEdit(artist)}>
                  Edit Profile
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => navigate(`/artist/storefront?artistId=${artist.userId}`)}
                  title="Customize this artist's storefront"
                >
                  <i className="fas fa-paint-brush"></i> Manage Storefront
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => fetchUnpaidItems(artist.userId)}
                  disabled={loadingUnpaidId === artist.userId}
                >
                  {loadingUnpaidId === artist.userId && <span className="btn-spinner" aria-hidden="true"></span>}
                  Load Unpaid Items
                </button>
              </div>
              {payoutSelections[artist.userId] && (
                <div className="payout-panel">
                  <h4>Mark Venmo Payout</h4>
                  <div className="form-group">
                    <label>Venmo Reference</label>
                    <input
                      value={payoutRefs[artist.userId] || ''}
                      onChange={(e) => setPayoutRefs((prev) => ({ ...prev, [artist.userId]: e.target.value }))}
                      placeholder="@handle or transaction note"
                    />
                  </div>
                  <div className="payout-items">
                    {Object.keys(payoutSelections[artist.userId]).length === 0 && (
                      <p>No unpaid items loaded.</p>
                    )}
                    {Object.keys(payoutSelections[artist.userId]).map((id) => (
                      <label key={id} className="payout-item">
                        <input
                          type="checkbox"
                          checked={payoutSelections[artist.userId][parseInt(id)] || false}
                          onChange={() => handlePayoutToggle(artist.userId, parseInt(id))}
                        />
                        <span>Order Item #{id}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleMarkPaid(artist.userId)}
                    disabled={markingPaidId === artist.userId}
                  >
                    {markingPaidId === artist.userId && <span className="btn-spinner" aria-hidden="true"></span>}
                    Mark Paid
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="spotlight-panel">
          <h2>Artist of the Month</h2>
          <div className="form-row">
            <select value={spotlightArtistId} onChange={(e) => setSpotlightArtistId(e.target.value)}>
              <option value="">Select an artist...</option>
              {artists.map((artist) => (
                <option key={artist.userId} value={artist.userId}>
                  {artist.displayName || artist.email}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" onClick={setArtistOfMonth} disabled={!spotlightArtistId || savingSpotlight}>
              {savingSpotlight && <span className="btn-spinner" aria-hidden="true"></span>}
              Set Artist of the Month
            </button>
          </div>
        </div>

        {editing && (
          <div className="modal-overlay" onClick={() => setEditing(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Edit Artist</h2>
              <div className="form-group">
                <label>Display Name</label>
                <input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Discount Percent</label>
                <input type="number" step="0.1" value={form.discountPercent} onChange={(e) => setForm({ ...form, discountPercent: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Upload Limit</label>
                <input type="number" value={form.artworkUploadLimit} onChange={(e) => setForm({ ...form, artworkUploadLimit: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Venmo Handle</label>
                <input value={form.venmoHandle} onChange={(e) => setForm({ ...form, venmoHandle: e.target.value })} />
              </div>
              <div className="actions-row">
                <button className="btn btn-primary" onClick={saveProfile} disabled={savingProfile}>
                  {savingProfile && <span className="btn-spinner" aria-hidden="true"></span>}
                  Save
                </button>
                <button className="btn btn-outline" onClick={() => setEditing(null)} disabled={savingProfile}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminArtists;
